import { type AccountOpeningAccountPayload, type AccountOpeningCustomerPayload, type AccountOpeningDTO } from '../../../models/AccountOpening'
import { ITEM_COPY_DEFAULTS } from '../descriptor/stepCopy'
import { pruneCitations } from './citations'
import { withoutBlanksFor } from './schemaValues'
import { type HolderNameSource, type WizardAccount, type WizardContext, type WizardHolder, type WizardParty } from './types'

export const partyKey = (name: string): string => name.trim().toLowerCase()

export const namedHolders = (accounts: WizardAccount[]): WizardHolder[] =>
  accounts.flatMap((account) => account.holders).filter((holder) => holder.name.trim().length > 0)

type PartyIdentity = Pick<WizardHolder, 'name' | 'contactId' | 'externalId'>

const samePerson = (a: PartyIdentity, b: PartyIdentity): boolean => {
  if (partyKey(a.name) !== partyKey(b.name)) {
    return false
  }
  if (a.contactId && b.contactId) {
    return a.contactId === b.contactId
  }
  return (a.externalId ?? null) === (b.externalId ?? null)
}

const partyOfHolder = (parties: WizardParty[], holder: PartyIdentity): WizardParty | undefined => parties.find((party) => samePerson(party, holder))

export const reconcileParties = (accounts: WizardAccount[], previous: WizardParty[]): WizardParty[] => {
  const parties: WizardParty[] = []

  namedHolders(accounts).forEach((holder) => {
    const existing = partyOfHolder(parties, holder)
    if (existing) {
      if (!existing.roleCodes.includes(holder.roleCode)) {
        existing.roleCodes.push(holder.roleCode)
      }
      existing.contactId = existing.contactId ?? holder.contactId
      existing.externalId = existing.externalId ?? holder.externalId
      return
    }
    const unclaimed = previous.filter((party) => !parties.some((taken) => taken.customerRefId === party.customerRefId))
    const carried = partyOfHolder(unclaimed, holder)
    const roleChanged = !!carried && carried.roleCodes.length > 0 && carried.roleCodes[0] !== holder.roleCode
    parties.push({
      customerRefId: carried?.customerRefId ?? crypto.randomUUID(),
      name: holder.name.trim(),
      contactId: holder.contactId ?? carried?.contactId ?? null,
      ...(holder.externalId ? { externalId: holder.externalId } : {}),
      ...(carried?.custodianDetailsOf ? { custodianDetailsOf: carried.custodianDetailsOf } : {}),
      ...(carried?.custodianDetailsAt ? { custodianDetailsAt: carried.custodianDetailsAt } : {}),
      roleCodes: [holder.roleCode],
      kycSchemaSnapshot: roleChanged ? {} : (carried?.kycSchemaSnapshot ?? {}),
      kycValues: carried?.kycValues ?? {},
      citations: carried?.citations ?? {},
    })
  })

  return parties
}

const rolesForAccount = (account: WizardAccount, parties: WizardParty[]): AccountOpeningAccountPayload['roles'] =>
  account.holders
    .filter((holder) => holder.name.trim().length > 0)
    .map((holder) => ({
      customerRefId: partyOfHolder(parties, holder)?.customerRefId ?? '',
      roleCode: holder.roleCode,
      ...(holder.relationship ? { relationship: holder.relationship } : {}),
    }))
    .filter((role) => role.customerRefId.length > 0)

export const buildAccountsPayload = (accounts: WizardAccount[], parties: WizardParty[]): AccountOpeningAccountPayload[] =>
  accounts.map((account) => {
    const values = withoutBlanksFor(account.schemaSnapshot, account.values)
    return {
      accountRefId: account.accountRefId,
      typeId: account.typeId,
      schemaSnapshot: account.schemaSnapshot,
      ...(account.schemaFetchedAt ? { schemaFetchedAt: account.schemaFetchedAt } : {}),
      values,
      citations: pruneCitations(account.values, values, account.citations),
      roles: rolesForAccount(account, parties),
    }
  })

export const partySchemaRole = (party: WizardParty): string | undefined => party.roleCodes[0]

export const linkedCoHolders = (accounts: WizardAccount[], party: WizardParty): WizardHolder[] =>
  accounts
    .filter((account) => account.holders.some((holder) => samePerson(party, holder)))
    .flatMap((account) => account.holders)
    .filter((holder) => !!holder.contactId && !samePerson(party, holder))

const soleContactId = (holders: WizardHolder[]): string | null => {
  const contactIds = [...new Set(holders.map((holder) => holder.contactId))]
  return contactIds.length === 1 ? contactIds[0] : null
}

export const clientContactIdOf = (coHolders: WizardHolder[], clientRoles: string[]): string | null => {
  const preferred = clientRoles.map((role) => coHolders.filter((holder) => holder.roleCode === role)).find((holders) => holders.length > 0)
  return soleContactId(preferred ?? coHolders)
}

export const buildCustomersPayload = (parties: WizardParty[]): AccountOpeningCustomerPayload[] =>
  parties.map((party) => {
    const kycValues = withoutBlanksFor(party.kycSchemaSnapshot, party.kycValues)
    return {
      customerRefId: party.customerRefId,
      contactId: party.contactId,
      ...(party.externalId ? { externalId: party.externalId } : {}),
      displayName: party.name,
      kycSchemaSnapshot: party.kycSchemaSnapshot,
      kycValues,
      citations: pruneCitations(party.kycValues, kycValues, party.citations),
    }
  })

export const connectorPayload = (ctx: WizardContext, accounts: WizardAccount[], parties: WizardParty[]): Record<string, unknown> => ({
  connector: ctx.connector,
  accounts: buildAccountsPayload(accounts, parties),
  customers: buildCustomersPayload(parties),
})

const savedPartyName = (
  customer: AccountOpeningCustomerPayload,
  index: number,
  resolveContactName: (contactId: string) => string,
  fallbackName: string = ITEM_COPY_DEFAULTS.partyFallbackName,
): string => {
  const contactName = customer.contactId ? resolveContactName(customer.contactId) : ''
  return customer.displayName?.trim() || contactName.trim() || fallbackName.replace('{index}', String(index + 1))
}

const holderNameSource = (party: WizardParty | undefined): HolderNameSource => {
  if (party?.contactId) {
    return 'contact'
  }
  return party?.name.trim() ? 'manual' : null
}

export interface LoadedDraft {
  accounts: WizardAccount[]
  parties: WizardParty[]
}

export const loadDraftIntoWizard = (
  dto: AccountOpeningDTO,
  selectionFor: (typeId: string) => Record<string, string>,
  resolveContactName: (contactId: string) => string,
  fallbackName?: string,
): LoadedDraft => {
  const parties: WizardParty[] = (dto.customers ?? []).map((customer, index) => ({
    customerRefId: customer.customerRefId,
    name: savedPartyName(customer, index, resolveContactName, fallbackName),
    contactId: customer.contactId ?? null,
    ...(customer.externalId ? { externalId: customer.externalId } : {}),
    roleCodes: [],
    kycSchemaSnapshot: customer.kycSchemaSnapshot ?? {},
    kycValues: customer.kycValues ?? {},
    citations: customer.citations ?? {},
  }))
  const partyById = new Map(parties.map((party) => [party.customerRefId, party]))

  const accounts: WizardAccount[] = (dto.accounts ?? []).map((account) => {
    return {
      accountRefId: account.accountRefId,
      selection: selectionFor(account.typeId),
      typeId: account.typeId,
      holders: (account.roles ?? []).map((role) => {
        const party = partyById.get(role.customerRefId)
        return {
          roleCode: role.roleCode,
          nameSource: holderNameSource(party),
          name: party?.name ?? '',
          contactId: party?.contactId ?? null,
          relationship: role.relationship,
          ...(party?.externalId ? { clientStatus: 'existing' as const, externalId: party.externalId } : {}),
        }
      }),
      schemaSnapshot: account.schemaSnapshot ?? {},
      schemaFetchedAt: account.schemaFetchedAt,
      values: account.values ?? {},
      citations: account.citations ?? {},
    }
  })

  return { accounts, parties: reconcileParties(accounts, parties) }
}
